import React, {useCallback, useEffect, useRef, useState} from "react";
import './CameraComponent.scss'
import {MaskType} from "./MaskType";
import 'image-capture/src/imagecapture';
import {FacingModeType} from "./FacingModeType";
import {isMobileDevice} from "../../../utils/WindowUtils";
import {ButtonComponent} from "../button/ButtonComponent";
import {ProgressBarComponent} from "../progress-bar/ProgressBarComponent";
import {useThemedIcons} from "../theme-provider/hooks/useThemedIcons";

interface CameraComponentProps {
    onSubmit: (blob: Blob) => void
    onPhotoTaken: () => void
    maskType: MaskType
    facingMode: FacingModeType
    tooltip: string | React.ReactNode
    identifierText: string
    isDocument?: boolean
    back?: boolean
    withRetake?: boolean
    retakeTooltip?: string
}

const CameraComponent: React.FC<CameraComponentProps> = ({ onSubmit, maskType, facingMode, tooltip, isDocument, withRetake, retakeTooltip, identifierText }: CameraComponentProps) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);

    const streamRef = useRef<MediaStream | null>(null);
    const imageCaptureRef = useRef<ImageCapture | null>(null);

    const streamTokenRef = useRef(0);
    const warmedUpRef = useRef(false);

    const [showPicture, setShowPicture] = useState(false);
    const [picture, setPicture] = useState<Blob | null>(null);
    const [maxHeight, setMaxHeight] = useState(0);
    const [maxWidth, setMaxWidth] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingCamera, setLoadingCamera] = useState(false);
    const [cameraReverted, setCameraReverted] = useState(false);
    const [cameras, setCameras] = useState<string[]>();
    const [currentCamera, setCurrentCamera] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState<boolean>(false);

    const { icons } = useThemedIcons();

    const supportsTakePhoto = (ic?: ImageCapture) =>
        !!ic && typeof (ic as any).takePhoto === 'function';
    const supportsGrabFrame = (ic?: ImageCapture) =>
        !!ic && typeof (ic as any).grabFrame === 'function';

    const wait = (ms: number) => new Promise(res => setTimeout(res, ms));

    const waitForVideoReady = async (video: HTMLVideoElement) => {
        let attempts = 30;
        while (attempts-- > 0 && (!video.videoWidth || !video.videoHeight)) {
            await wait(50);
        }
    };

    const stopCamera = useCallback(() => {
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        imageCaptureRef.current = null;
    }, []);

    // Get capabilities — can set state, but is only called by startCamera
    const getCameraCapabilities = useCallback(async () => {
        const constraints: MediaStreamConstraints = { audio: false, video: { facingMode } };
        const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        const videoTrack = mediaStream.getVideoTracks()[mediaStream.getVideoTracks().length - 1];
        const ic = new ImageCapture(videoTrack);
        const caps = await ic.getPhotoCapabilities();

        setMaxHeight(caps.imageHeight.max);
        setMaxWidth(caps.imageWidth.max);

        mediaStream.getTracks().forEach(track => track.stop());

        const devices = await navigator.mediaDevices.enumerateDevices();
        const camerasFound = devices
            .filter(d =>
                d.kind === "videoinput" &&
                (d.label.toLowerCase().includes("back") ||
                    d.label.toLowerCase().includes("rear") ||
                    d.label.toLowerCase().includes("environment")))
            .map(d => d.deviceId);

        setCameras(camerasFound);
        setCurrentCamera(prev => prev ?? camerasFound[0] ?? null);
    }, [facingMode]);

    const startCamera = useCallback(async (revert?: boolean, firstLoad?: boolean) => {
        try {
            let idealHeight = 1080;
            let idealWidth: number;
            let aspectRatio: number;

            if (firstLoad) setLoadingCamera(true);

            if (facingMode === FacingModeType.USER) {
                // if (revert) {
                //     aspectRatio = 4 / 3;
                //     idealHeight = 1920;
                //     idealWidth = 1440;
                // } else {
                //     aspectRatio = 3 / 4;
                //     idealHeight = 1440;
                //     idealWidth = 1920;
                // }
                aspectRatio = 3 / 4;
                idealHeight = 1080;
                idealWidth = 1920;
            } else {
                // ensure we have caps
                if (maxWidth === 0 || maxHeight === 0) {
                    await getCameraCapabilities();
                }

                const h = maxHeight || 1080;
                const w = maxWidth || 1920;

                if (maxHeight === 0 || maxWidth === 0) {
                    aspectRatio = w / h;
                    setMaxHeight(h);
                    setMaxWidth(w);
                    idealHeight = h;
                    idealWidth = w;
                } else {
                    aspectRatio = maxWidth / maxHeight;
                    idealHeight = 1080;
                    idealWidth = idealHeight * aspectRatio;
                }
            }

            const constraints: MediaStreamConstraints = {
                audio: false,
                video: currentCamera
                    ? {
                        deviceId: { exact: currentCamera },
                        width: { ideal: idealWidth, max: idealWidth },
                        height: { ideal: idealHeight, max: idealHeight },
                        facingMode,
                        aspectRatio
                    }
                    : {
                        width: { ideal: idealWidth, max: idealWidth },
                        height: { ideal: idealHeight, max: idealHeight },
                        facingMode,
                        aspectRatio
                    }
            };

            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

            const video = videoRef.current;
            if (video) video.srcObject = mediaStream;

            const videoTrack = mediaStream.getVideoTracks()[mediaStream.getVideoTracks().length - 1];
            const ic = new ImageCapture(videoTrack);

            streamRef.current = mediaStream;
            imageCaptureRef.current = ic;
        } catch (err) {
            console.error("Error accessing camera:", err);
        }
    }, [currentCamera, facingMode, maxHeight, maxWidth, getCameraCapabilities]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            await startCamera(false, true);
        })();
        return () => {
            if (!cancelled) stopCamera();
            cancelled = true;
        };
    }, [startCamera, stopCamera]);

    const switchCamera = async (deviceId: string) => {
        setCurrentCamera(deviceId);
        await startCamera();
    }

    const onCameraLoad = async () => {
        setLoadingCamera(true)
        const video = videoRef.current;
        if (!video) return;

        // Your revert logic first; run only for USER facing
        if (!cameraReverted && facingMode === FacingModeType.USER) {
            if (video.videoWidth > video.videoHeight) {
                setCameraReverted(true);
                stopCamera();
                await startCamera(true, true);
                return;
            }
        }
        // Then do the warm-up throwaway capture
        await warmUpExposure();
    }

    const processCapturedImage = async (
        imageBitmap: ImageBitmap,
        previewWidth: number,
        previewHeight: number
    ): Promise<Blob> => {
        const previewAspectRatio = previewWidth / previewHeight;
        const imageAspectRatio = imageBitmap.width / imageBitmap.height;

        let resultWidth = 0;
        let resultHeight = 0;
        let imageWidth = imageBitmap.width;
        let imageHeight = imageBitmap.height;

        // Crop the image to match preview aspect ratio
        if (imageAspectRatio > previewAspectRatio) {
            // Image is wider than preview — crop sides
            imageWidth = imageHeight * previewAspectRatio;
            resultWidth = (imageBitmap.width - imageWidth) / 2;
        } else {
            // Image is taller than preview — crop top/bottom
            imageHeight = imageWidth / previewAspectRatio;
            resultHeight = (imageBitmap.height - imageHeight) / 2;
        }

        // Create a canvas matching the preview resolution
        const canvas = document.createElement('canvas');
        canvas.width = previewWidth;
        canvas.height = previewHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error("Failed to get canvas context");

        // Draw cropped portion of the image to canvas
        ctx.drawImage(imageBitmap, resultWidth, resultHeight, imageWidth, imageHeight, 0, 0, canvas.width, canvas.height);

        // Convert canvas to blob
        return new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (!blob) {
                    reject(new Error("Failed to create blob from canvas"));
                } else {
                    resolve(blob);
                }
            }, 'image/jpeg');
        });
    }

    const captureImage = async () => {
        setLoading(true);
        const imageCapturer = imageCaptureRef.current;
        if (!imageCapturer) { setLoading(false); return; }

        try {
            const video = videoRef.current;
            if (!video) throw new Error('Video not ready');

            let imageBitmap: ImageBitmap | null = null;

            if (supportsTakePhoto(imageCapturer)) {
                const photo = await (imageCapturer as any).takePhoto();
                imageBitmap = await createImageBitmap(photo);
            } else if (supportsGrabFrame(imageCapturer)) {
                imageBitmap = await (imageCapturer as any).grabFrame();
            } else {
                // Final fallback: draw from video to canvas
                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth || 640;
                canvas.height = video.videoHeight || 480;
                const ctx = canvas.getContext('2d');
                if (!ctx) throw new Error('No 2D context');
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                const blob = await new Promise<Blob>((resolve, reject) =>
                    canvas.toBlob(b => b ? resolve(b) : reject(new Error('canvas.toBlob failed')), 'image/jpeg')
                );
                imageBitmap = await createImageBitmap(blob);
            }
            const processedBlob = await processCapturedImage(
                imageBitmap!!,
                video.videoWidth,
                video.videoHeight
            );
            setPicture(processedBlob);
            if (withRetake && imgRef.current) {
                setShowPicture(true);
                imgRef.current.src = URL.createObjectURL(processedBlob);
            } else {
                onSubmit(processedBlob);
            }
        } catch {
            // swallow and reset loader
        } finally {
            setLoading(false);
        }
    };

    const warmUpExposure = async () => {
        const imageCapturer = imageCaptureRef.current;
        if (!imageCapturer) { setLoading(false); return; }
        if (warmedUpRef.current) { setLoadingCamera(false); return; }

        const tokenAtStart = streamTokenRef.current;
        const video = videoRef.current;
        if (!video) return;

        await waitForVideoReady(video);
        // small delay to let AE/AF stabilize
        await wait(150);

        if (tokenAtStart !== streamTokenRef.current) return;
        try {
            if (supportsTakePhoto(imageCapturer)) {
                // Take one photo and discard to wake AE; swallow any error.
                await (imageCapturer as any).takePhoto().catch(() => {});
            } else if (supportsGrabFrame(imageCapturer)) {
                await (imageCapturer as any).grabFrame().catch(() => {});
                await wait(80);
                await (imageCapturer as any).grabFrame().catch(() => {});
            } else {
                // Fallback: draw from <video> a couple times
                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth || 640;
                canvas.height = video.videoHeight || 480;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                    await wait(80);
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                }
            }
        } finally {
            warmedUpRef.current = true;
            setLoadingCamera(false)
        }
    };

    const retakePicture = async () => {
        setShowPicture(false)
    }

    const sendPicture = () => {
        if (picture != null) {
            onSubmit(picture)
            setSubmitted(true)
            stopCamera()
        }
    }

    const createPictureTakenButtonsComponent = () => {
        return (
            <div className={`picture-taken-buttons-container picture-taken-buttons-container-document`}>
                {isMobileDevice() ? <div
                    className={`camera-tooltip photo-taken font-md full-row font-white width-full`}>
                    {retakeTooltip ? retakeTooltip : 'Happy with your photo?'}</div> : ''
                }
                <div className={`width-full flex-end refresh-button`}>
                    {isMobileDevice() ?
                        <div className={`camera-button-outward border-white button`}
                             onClick={() => retakePicture()}>

                            <div className={`camera-button-inward flex-center`}>
                                {icons.refresh}
                            </div>
                        </div>
                        :
                        <div className={`camera-desktop-button`}>
                            <ButtonComponent label="No, retake" secondary
                                             onButtonClick={() => retakePicture()}/>
                        </div>
                    }
                </div>
                <div className={`width-full flex-start submit-button`}>
                    {isMobileDevice() ?
                        <div className={`camera-button-outward button border-white ${submitted ? 'disabled' : ''}`}
                             onClick={() => sendPicture()}>
                            <div className={`camera-button-inward background-white flex-center`}>
                                {icons.check}
                            </div>
                        </div>
                        :
                        <div className={`camera-desktop-button`}>
                            <ButtonComponent label="Yes, continue"
                                             onButtonClick={() => sendPicture()} disableOnClick/>
                        </div>
                    }

                </div>
            </div>
        )
    }

    const createPictureTakenComponent = () => (
        <div className={`flex-center ${isMobileDevice() ? 'camera-mobile-container': ''} ${withRetake && showPicture ? '' : 'hide'}`}>
            <div
                className={`${isMobileDevice() ? 'camera-container' : 'camera-desktop-container'}`}>
                <div
                    className={`${isMobileDevice() ? 'display-grid' : 'camera-desktop-container flex-center'} background-white width-full`}>
                    {isMobileDevice() ? '' :
                        <div
                            className={`camera-tooltip font-md primary-color`}>{retakeTooltip ? retakeTooltip : 'Are you happy with your photo?'}</div>}
                    <img alt="preview"
                         className={`picture-taken ${facingMode === FacingModeType.USER ? 'facing-mode-user' : ''}`}
                         ref={imgRef}/>
                    {createPictureTakenButtonsComponent()}
                </div>
            </div>


        </div>


    )

    const createMobileComponent = () => (
        <div className={`container`}>
            <div className={`flex-center camera-mobile-container ${withRetake && showPicture ? 'hide' : ''}
             ${facingMode === FacingModeType.ENVIRONMENT ? 'camera-container-document' : ''}`}>
                <>
                    <div className={`font-white font-md-plus width-full height-full flex-center camera-loading ${loadingCamera ? '' : 'hidden'}`}>
                        Accessing camera
                    </div>
                    <div
                        className={`camera-container ${facingMode === FacingModeType.USER ? 'selfie' : 'camera-container-document'} ${loadingCamera ? 'hidden' : ''}`}>
                        {!loadingCamera && <div className={`camera-position-indicator`}>
                            {identifierText}
                        </div>}
                        {!loadingCamera ? (
                            <>
                                <div className={`focus-slider`}>
                                    <div className={`picture-frame`}>
                                        <div className={`picture-frame-background`}
                                             style={{gridColumn: "1 / -1", gridRow: "1 / 2"}}></div>
                                        <div className={`picture-frame-background`}
                                             style={{gridColumn: "1 / 2", gridRow: "2 / -1"}}></div>
                                        <div
                                            className={`${maskType === MaskType.DOCUMENT ? "document-overlay" : ""}`}>
                                        </div>
                                        {cameras && (
                                            <div className={`camera-list-container`}>
                                                <div className={`camera-list-title`}>Adjust focus</div>
                                                {cameras.map((camera, index) => (
                                                    <div
                                                        className={`camera-list-item font-600 ${camera === currentCamera ? 'primary-background font-black' : ''}`}
                                                        onClick={() => switchCamera(camera)}>{index + 1}</div>
                                                ))}
                                            </div>
                                        )}
                                        <div className={`picture-frame-background`}
                                             style={{gridColumn: "3 / -1", gridRow: "2 / -1"}}></div>
                                    </div>

                                    <div
                                        className={`camera-tooltip font-md font-white flex-center`}>{tooltip}</div>
                                    <div className={`picture-frame-background flex-center`}>
                                        <div className={`camera-button-outward button border-white`}
                                             onClick={() => captureImage()}>
                                            <div className={`camera-button-inward background-white`}></div>
                                        </div>
                                    </div>

                                </div>
                            </>
                        ) : ('')}
                        <video
                            className={`${facingMode === FacingModeType.USER ? 'facing-mode-user' : ''}`}
                            onLoadedDataCapture={() => onCameraLoad()}
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                        />

                        {
                            loading ?
                                <div className={`camera-progress-bar-container`}>
                                    <div className={`camera-progress-bar`}>
                                        <ProgressBarComponent animated timeToComplete={1.5} height="10px" backgroundColor="#FFF"/>
                                    </div>
                                </div> :
                                ''
                        }
                        <div
                            className={`${maskType === MaskType.DOCUMENT ? "" : "oval-overlay"}`}>
                        </div>


                    </div>
                </>
            </div>
            {createPictureTakenComponent()}
        </div>
    )

    const createDesktopComponent = () => (
        <div className={`background-full`}>
            <div className={`flex-center ${withRetake && showPicture ? 'hide' : ''} camera-desktop-wrapper`}>
                <div className={`background-white camera-desktop-container flex-center`}>
                    <div className={`camera-tooltip font-md flex-center`}>{tooltip}</div>
                    <div className={`camera-container`}>
                        <div className={`camera-position-indicator`}>
                            {identifierText}
                        </div>
                        <video
                            className={`facing-mode-user`}
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                        />
                        {
                            loading ?
                                <div className={`camera-progress-bar-container`}>
                                    <div className={`camera-progress-bar`}>
                                        <ProgressBarComponent animated timeToComplete={1.5} height="10px"/>
                                    </div>
                                </div> :
                                ''
                        }
                        <div
                            className={`${maskType === MaskType.DOCUMENT ? "document-overlay" : "oval-overlay"}`}>
                        </div>

                    </div>
                </div>
                <div className={`camera-desktop-button`}>
                    <ButtonComponent label={`${isDocument ? 'Take a photo of ID' : 'Take photo'}`}
                                     onButtonClick={() => captureImage()}/>
                </div>
            </div>
            {createPictureTakenComponent()}
        </div>
    )

    return isMobileDevice() ? createMobileComponent() : createDesktopComponent()

}

export default CameraComponent;